import { useState } from "react"
import { useIntl } from "react-intl"

import classNames from "classnames"

import { Dialog, StaffHeading3 } from "@doktor-se/bones-ui"
import { AssignedConversation, Staff } from "@doktor-se/bones-ui/dist/web-shared/types"

import { claimForOther, reassignBooking, reassignConversation, reopenConversation } from "api"
import { staffListForAssigning } from "config"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { BookedAppointment } from "reducers/conversations/conversations.reducer"
import { SearchPageConversation } from "types"

import ConfirmReassign from "./components/ConfirmReassign/ConfirmReassign"
import StaffSearch from "./components/StaffSearch/StaffSearch"

import styles from "./ReassignDialog.module.scss"

interface ReassignDialogProps {
  isOpen: boolean
  conversation: BookedAppointment | AssignedConversation | SearchPageConversation
  onClose: () => void
  header?: string
  onSuccess?: () => void
}

const ReassignDialog = ({ isOpen, conversation, onClose, header, onSuccess }: ReassignDialogProps) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const authUser = useAppSelector(state => state.auth.user)
  const { activeStaff } = useAppSelector(state => state.users)
  const user = activeStaff.find(s => s.id === authUser?.id)
  const filteredStaffList = staffListForAssigning(activeStaff, user?.data?.clinic).filter(
    s => s.id !== conversation.assignedStaffId
  )

  const [dialogState, setDialogState] = useState<"search" | "confirm">("search")
  const [selectedStaff, setSelectedStaff] = useState<Staff>()

  const onSelectStaff = (selectedStaff: Staff) => {
    setSelectedStaff(activeStaff.find(staff => staff.id === selectedStaff.id))
    setDialogState("confirm")
  }

  const reassignConfirmed = async (assignToId: string) => {
    try {
      if (conversation.state === "booked") {
        if ("bookingId" in conversation) {
          await dispatch(
            reassignBooking(
              {
                booking_ids: [conversation.bookingId],
                staff_id: assignToId,
                isInGroupCalendar: false
              },
              { showToast: true, intl }
            )
          )
        } else if (conversation.metadata?.bookingId) {
          await dispatch(
            reassignBooking(
              {
                booking_ids: [conversation.metadata?.bookingId],
                staff_id: assignToId,
                isInGroupCalendar: false
              },
              { showToast: true, intl }
            )
          )
        }
      } else if (conversation.state === "closed") {
        if (!conversation.assignedStaffId) {
          await dispatch(claimForOther(conversation, assignToId))
        } else {
          await dispatch(reopenConversation(conversation.id, assignToId))
        }
      } else {
        await dispatch(
          reassignConversation(
            {
              conversation,
              assignedId: assignToId
            },
            {
              showToast: true,
              intl
            }
          )
        )
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }

      onClose()
      setDialogState("search")
    } catch (error) {
      // Error handling is already done in the API functions
      console.error("Reassignment failed:", error)
    }
  }

  const onCloseDialog = () => {
    setDialogState("search")
    onClose()
  }

  return (
    <Dialog
      isOpen={isOpen}
      offset={{ top: "100px" }}
      onClose={onCloseDialog}
      closeAriaLabel={intl.formatMessage({ id: "modal.close" })}
      contentContainerClassName={classNames({ [styles.contentContainer]: dialogState === "search" })}
      fixedWidth>
      <StaffHeading3 margin={{ bottom: "var(--size-200)" }}>
        {header ? header : intl.formatMessage({ id: "search.new.title.assign" })}
      </StaffHeading3>

      {dialogState === "search" && <StaffSearch staffList={filteredStaffList} onSelectStaff={onSelectStaff} />}

      {dialogState === "confirm" && selectedStaff && (
        <ConfirmReassign
          selected={selectedStaff}
          onConfirm={() => reassignConfirmed(selectedStaff?.id!)}
          onCancel={() => {
            setDialogState("search")
          }}
        />
      )}
    </Dialog>
  )
}

export default ReassignDialog
